import React, { useState } from "react";
import { GenerateBulkCodesDto } from "@eagerdog/interfaces";

import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import { toast } from "../../../components/Toast/ToastManager";
import { apiService } from "../../../services/api.service";

import styles from "./GenerateBulkCodesModal.module.scss";

interface IProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface IFormData {
  batchName: string;
  count: number;
  expiresAt: string;
}

interface IFormErrors {
  batchName?: string;
  count?: string;
  expiresAt?: string;
}

const GenerateBulkCodesModal: React.FC<IProps> = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState<IFormData>({
    batchName: "",
    count: 100,
    expiresAt: ""
  });

  const [errors, setErrors] = useState<IFormErrors>({});
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const validateForm = (): boolean => {
    const newErrors: IFormErrors = {};

    // Validate batch name
    if (!formData.batchName.trim()) {
      newErrors.batchName = "Batch name is required";
    } else if (formData.batchName.trim().length < 3) {
      newErrors.batchName = "Batch name must be at least 3 characters";
    }

    // Validate count
    if (!formData.count || formData.count < 1) {
      newErrors.count = "Count must be at least 1";
    } else if (formData.count > 10000) {
      newErrors.count = "Count cannot exceed 10,000";
    }

    // Validate expiration date (optional)
    if (formData.expiresAt) {
      const expirationDate = new Date(formData.expiresAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (expirationDate <= today) {
        newErrors.expiresAt = "Expiration date must be in the future";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsGenerating(true);

    try {
      const dto: GenerateBulkCodesDto = {
        batch_name: formData.batchName,
        count: formData.count,
        expires_at: formData.expiresAt ? new Date(formData.expiresAt) : undefined
      };

      const response = await apiService.generateBulkDiscountCodes(dto);

      toast.show({
        title: "Success",
        content: `Generated ${response.codes.length} discount codes for batch "${formData.batchName}"`,
        duration: 5000,
        type: "success"
      });

      onSuccess();
    } catch (error: any) {
      console.error('Error generating bulk codes:', error);
      toast.show({
        title: "Error",
        content: error?.response?.data?.message || "Failed to generate discount codes. Please try again.",
        duration: 5000,
        type: "fail"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleInputChange = (field: keyof IFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  return (
    <div className={styles.modalWrapper}>
      <div className={styles.GenerateBulkCodesModal}>
        <div className={styles.header}>
          <h2>Generate Bulk Discount Codes</h2>
          <p>Create a new batch of one-time use discount codes that will zero out Eager Dog fees.</p>
        </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <Input
            label="Batch Name *"
            type="text"
            value={formData.batchName}
            onChange={(e) => handleInputChange("batchName", e.target.value)}
            placeholder="e.g., Q4 2024 Growth Campaign"
          />
          {errors.batchName && <div className={styles.error}>{errors.batchName}</div>}
        </div>

        <div className={styles.formGroup}>
          <Input
            label="Number of Codes *"
            type="number"
            value={formData.count.toString()}
            onChange={(e) => handleInputChange("count", parseInt(e.target.value) || 0)}
            placeholder="100"
            min={1}
            max={10000}
          />
          {errors.count && <div className={styles.error}>{errors.count}</div>}
          <div className={styles.hint}>Maximum: 10,000 codes per batch</div>
        </div>

        <div className={styles.formGroup}>
          <Input
            label="Expiration Date (Optional)"
            type="date"
            value={formData.expiresAt}
            onChange={(e) => handleInputChange("expiresAt", e.target.value)}
          />
          {errors.expiresAt && <div className={styles.error}>{errors.expiresAt}</div>}
          <div className={styles.hint}>Leave empty for codes that never expire</div>
        </div>

        <div className={styles.actions}>
          <Button
            type="button"
            onClick={onCancel}
            className={styles.cancelButton}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className={styles.generateButton}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : `Generate ${formData.count} Codes`}
          </Button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default GenerateBulkCodesModal; 